import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { ALPHA, FONT_SIZE, MOTION, RADIUS } from "../../../../theme/tokens";

/** Which side a line belongs to — drives whether an exclusive line reads as removed or added. */
export type ValueDiffSide = "current" | "incoming";

/**
 * How the two sides relate.
 *
 * - `replace` (default) — the incoming value REPLACES the current one wholesale. Lines present on only
 *   one side are marked: removed on the current side, added on the incoming side. This is the mode that
 *   makes destruction visible, which is the whole reason a list-shaped value gets a before *and* an
 *   after rather than just an after.
 * - `plain` — show both sides without per-line marks, for a comparison where nothing is being lost
 *   (a preview beside a reference, two candidates side by side).
 */
export type ValueDiffMode = "replace" | "plain";

type Props = {
	/** Already-translated caption over the left/before panel (e.g. "Current"). */
	currentLabel: string;
	/** Already-translated caption over the right/after panel (e.g. "Incoming"). */
	incomingLabel: string;
	/**
	 * The two values as display LINES — one entry per rendered row. A scalar is a single-entry array; a
	 * list-shaped value is one entry per item, so the reader sees the whole list, not a count.
	 *
	 * Lines are pre-formatted by the caller (this component owns comparison and presentation, never
	 * formatting or a vocabulary), and compared by exact string equality.
	 */
	currentLines: string[];
	incomingLines: string[];
	/** Already-translated stand-in for a side with no lines at all (e.g. "Not set"). */
	emptyLabel: string;
	mode?: ValueDiffMode;
	/**
	 * Force the two panels to stack. Omitting it is NOT "always side by side": the default already
	 * stacks below the `md` breakpoint, because two panels sharing a narrow column squeeze each value
	 * into a two-word ribbon — a rule line then wraps over six lines and the before/after comparison,
	 * which is the entire point, stops being readable at a glance. Pass `true` when the component sits
	 * in a narrow container on a wide screen, where the viewport breakpoint can't know that.
	 */
	stacked?: boolean;
	/** Cap the panel height; past it the lines scroll inside their own panel rather than growing the row. */
	maxHeight?: number;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

type LineState = "same" | "removed" | "added";

const LINE_TEST_ID_SUFFIX: Record<LineState, string> = { same: "same", removed: "removed", added: "added" };

/**
 * A line's state against the other side. Counted rather than set-tested so a value that legitimately
 * repeats a line (two identically-named rules) doesn't read as unchanged once the second copy is gone.
 */
function lineStates(own: string[], other: string[], side: ValueDiffSide, mode: ValueDiffMode): LineState[] {
	if (mode === "plain") return own.map(() => "same");
	const remaining = new Map<string, number>();
	for (const line of other) remaining.set(line, (remaining.get(line) ?? 0) + 1);
	const exclusive: LineState = side === "current" ? "removed" : "added";
	return own.map((line) => {
		const left = remaining.get(line) ?? 0;
		if (left === 0) return exclusive;
		remaining.set(line, left - 1);
		return "same";
	});
}

const Panel: FC<{
	label: string;
	lines: string[];
	states: LineState[];
	emptyLabel: string;
	/**
	 * Carry the reader's eye. In `replace` mode the per-line marks already do that, so nothing is
	 * emphasized; in `plain` mode nothing else would, and a comparison where both sides render in the
	 * same muted ink makes the reader work out which one is the proposal.
	 */
	emphasized: boolean;
	maxHeight?: number;
	dataTestId?: string;
}> = ({ label, lines, states, emptyLabel, emphasized, maxHeight, dataTestId }) => {
	// Keys are built here rather than at the JSX site because a line's identity genuinely INCLUDES its
	// position: the same line can appear twice (two rules with the same name), and the array is
	// render-order only — never reordered or spliced.
	const keyed = lines.map((line, index) => ({ key: `${index}-${line}`, line, state: states[index] ?? ("same" as LineState) }));
	return (
		<Box
			data-testid={dataTestId}
			sx={(theme) => ({
				flex: "1 1 0",
				minWidth: 0,
				display: "flex",
				flexDirection: "column",
				gap: 0.5,
				p: 0.75,
				borderRadius: `${RADIUS.CARD}px`,
				backgroundColor: alpha(theme.palette.text.primary, ALPHA.DIFF_PANEL_TINT),
			})}
		>
			<Typography
				component="span"
				sx={{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "text.secondary" }}
			>
				{label}
			</Typography>
			{lines.length === 0 ? (
				<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, color: "text.disabled", fontStyle: "italic" }}>
					{emptyLabel}
				</Typography>
			) : (
				<Box
					component="ul"
					sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: "1px", ...(maxHeight ? { maxHeight, overflowY: "auto" } : {}) }}
				>
					{keyed.map(({ key, line, state }) => (
						<Box
							key={key}
							component="li"
							data-testid={dataTestId ? `${dataTestId}-line-${LINE_TEST_ID_SUFFIX[state]}` : undefined}
							sx={(theme) => {
								const accent = state === "removed" ? theme.palette.error : theme.palette.success;
								return {
									fontSize: FONT_SIZE.SECONDARY,
									lineHeight: 1.45,
									wordBreak: "break-word",
									borderRadius: `${RADIUS.DIFF_LINE}px`,
									px: state === "same" ? 0 : 0.5,
									transition: `background-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}`,
									"@media (prefers-reduced-motion: reduce)": { transition: "none" },
									...(state === "same"
										? { color: emphasized ? "text.primary" : "text.secondary", fontWeight: emphasized ? 600 : 400 }
										: {
												backgroundColor: alpha(accent.main, ALPHA.DIFF_LINE_TINT),
												color: accent.dark,
												fontWeight: 600,
												// Not colour-alone: a removed line is also struck through.
												...(state === "removed" ? { textDecoration: "line-through" } : {}),
											}),
								};
							}}
						>
							{/* A leading sign so the two states are distinguishable without colour or strikethrough. */}
							{state !== "same" && (
								<Box component="span" aria-hidden sx={{ fontFamily: "monospace", mr: 0.5 }}>
									{state === "removed" ? "−" : "+"}
								</Box>
							)}
							{line}
						</Box>
					))}
				</Box>
			)}
		</Box>
	);
};

/**
 * Two values shown **side by side** — what a field holds now, and what it would hold — with the lines
 * that only exist on one side marked as removed or added.
 *
 * The problem it solves: a list-shaped value (a set of break rules, a wage-differential table) is
 * REPLACED wholesale by an import or a bulk edit, so showing only the incoming list hides the fact that
 * four existing rules are about to disappear. Rendering the whole before and the whole after, with the
 * disappearing lines struck through in error ink, makes the destruction readable before it is approved
 * rather than discoverable afterwards.
 *
 * Purely presentational and prop-driven: the caller formats values into lines and owns every string, so
 * this stays generic over any "review this change before applying it" surface — an integration import,
 * a bulk edit preview, a revision comparison. Inside a `SystemDataGrid` cell, pair it with
 * `dataGridProps.getRowHeight: () => "auto"`: a value line wraps to however many line boxes the column
 * width gives it, so any height computed from a line count is wrong at some width and clips exactly the
 * removals this component exists to show. (`SystemDataGrid`'s warning against `"auto"` is scoped to its
 * own settled detail rows; real rows defer to the caller's function.) `DIFF_REVIEW` holds the bounds.
 */
export const ValueDiffPair: FC<Props> = ({
	currentLabel,
	incomingLabel,
	currentLines,
	incomingLines,
	emptyLabel,
	mode = "replace",
	stacked = false,
	maxHeight,
	dataTestId,
	sx,
}) => (
	<Box
		data-testid={dataTestId}
		sx={[
			{ display: "flex", flexDirection: stacked ? "column" : { xs: "column", md: "row" }, gap: 1, alignItems: "stretch", width: "100%", minWidth: 0 },
			...(Array.isArray(sx) ? sx : [sx]),
		]}
	>
		<Panel
			label={currentLabel}
			lines={currentLines}
			states={lineStates(currentLines, incomingLines, "current", mode)}
			emptyLabel={emptyLabel}
			emphasized={false}
			maxHeight={maxHeight}
			dataTestId={dataTestId ? `${dataTestId}-current` : undefined}
		/>
		<Panel
			label={incomingLabel}
			lines={incomingLines}
			states={lineStates(incomingLines, currentLines, "incoming", mode)}
			emptyLabel={emptyLabel}
			emphasized={mode === "plain"}
			maxHeight={maxHeight}
			dataTestId={dataTestId ? `${dataTestId}-incoming` : undefined}
		/>
	</Box>
);
